import { Outlet, createFileRoute, useNavigate } from "@tanstack/react-router";
import { useEffect } from "react";
import { toast } from "@/lib/toast";
import { ConceptRail } from "@/components/concepts/ConceptRail";
import { useConcept } from "@/hooks/useConcepts";
import { useSetupRedirect } from "@/hooks/useSetupRedirect";
import { usePapers } from "@/hooks/usePapers";
import { usePapersCount } from "@/hooks/usePapersCount";
import { useEvidenceStats } from "@/hooks/useClaims";
import { Skeleton } from "@/components/ui/skeleton";
import { useEvidenceStore } from "@/stores/evidence-store";

export const Route = createFileRoute("/concepts/$conceptId")({
  component: ConceptLayout,
});

function ConceptLayout() {
  const { conceptId } = Route.useParams();
  const navigate = useNavigate();
  const { data: concept, isLoading, isError, error } = useConcept(conceptId);
  const { data: trawledData } = usePapers(conceptId, {
    role: "trawled",
    mode: "all",
    shape: "analytics",
  });
  const { data: counts } = usePapersCount(conceptId);
  const { data: evidenceStats } = useEvidenceStats(conceptId);
  const claimCount = evidenceStats?.unique_papers_with_evidence ?? null;
  const pendingCount = useEvidenceStore((s) => s.pendingEvidence.length);

  // A concept that 404s — deleted, or a stale URL belonging to another user
  // (e.g. after a dev user-switch) — should bounce to the dashboard rather than
  // stranding the user on a dead page while sibling queries 404. Only redirect
  // on a definitive 404; transient errors fall through to the inline fallback.
  const notFound =
    isError &&
    (error as { response?: { status?: number } } | null)?.response?.status === 404;
  useEffect(() => {
    if (notFound) {
      toast.error("That concept no longer exists.");
      navigate({ to: "/dashboard", replace: true });
    }
  }, [notFound, navigate]);
  // Before any early return (house rule: hooks never after a return).
  useSetupRedirect(concept);

  if (isLoading || notFound) {
    return (
      <div className="flex gap-6">
        <Skeleton className="h-[600px] w-[224px] shrink-0" />
        <Skeleton className="h-[600px] flex-1" />
      </div>
    );
  }

  if (!concept) {
    return <p className="text-muted-foreground">Concept not found.</p>;
  }

  return (
    <div className="flex gap-6">
      <ConceptRail
        concept={concept}
        papers={trawledData?.papers}
        seedCount={counts?.foundational ?? 0}
        claimCount={claimCount}
        pendingCount={pendingCount}
      />
      <div className="min-w-0 flex-1 space-y-4">
        <Outlet />
      </div>
    </div>
  );
}
